import type { ColumnDef } from '@tanstack/react-table'
import type { SurveyResponseSummaryDTO } from '@/entities/survey'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from '@/shared/libs/generate-path'
import { useSurveyResponsesByUserQuery, useSurveyResponsesQuery } from '@/entities/survey'
import { useTenantByIdQuery, useTenantsQuery } from '@/entities/tenant'
import { formatDateTime } from '@/shared/api/utils/helpers'
import { DEFAULT_PAGE_SIZE } from '@/shared/constants'
import { useAdminScope } from '@/shared/hooks/use-admin-scope'
import { useScopedAdminPath } from '@/shared/hooks/use-scoped-admin-path'
import { Badge } from '@/shared/ui/badge'
import { Card, CardContent } from '@/shared/ui/card'
import { DataTable, DataTableFilterField, DataTableToolbar } from '@/shared/ui/data-table'
import { PageContainer } from '@/shared/ui/page-container'
import { PageHeader } from '@/shared/ui/page-header'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/shared/ui/sheet'
import { Skeleton } from '@/shared/ui/skeleton'

interface TenantOptionItem {
  label: string
  value: string
}

export function SurveyResponsesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const scopedAdminPath = useScopedAdminPath()
  const params = useParams({ strict: false })
  const routeTenantId = params.tenantId
  const { activeTenantId = '', isSuperAdmin, isTenantAdmin, setActiveTenantId } = useAdminScope()

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [selectedUserId, setSelectedUserId] = useState('')

  const { data: tenantsResponse } = useTenantsQuery(
    { page: 1, pageSize: 100, isActive: true },
    { enabled: isSuperAdmin },
  )

  const tenantOptions = useMemo<TenantOptionItem[]>(
    () =>
      (tenantsResponse?.data ?? [])
        ?.filter((tenant) => tenant.name !== 'Default')
        .map((tenant) => ({
          label: tenant.name,
          value: tenant.id,
        })),
    [tenantsResponse?.data],
  )

  const currentTenantId = useMemo(() => {
    if (isTenantAdmin) return activeTenantId
    if (routeTenantId && tenantOptions.some((opt) => opt.value === routeTenantId))
      return routeTenantId
    if (activeTenantId && tenantOptions.some((opt) => opt.value === activeTenantId))
      return activeTenantId
    return tenantOptions[0]?.value ?? ''
  }, [activeTenantId, isTenantAdmin, routeTenantId, tenantOptions])

  const { data: tenantResponse, isLoading: tenantLoading } = useTenantByIdQuery(currentTenantId)
  const tenantName = tenantResponse?.data?.name ?? ''

  const queryTenantId = isSuperAdmin ? currentTenantId : ''

  const responsesQuery = useSurveyResponsesQuery(
    queryTenantId,
    { current: page, pageSize },
    { enabled: !!currentTenantId },
  )
  const detailQuery = useSurveyResponsesByUserQuery(
    queryTenantId,
    selectedUserId,
    { current: 1, pageSize: 100 },
    { enabled: !!currentTenantId && !!selectedUserId },
  )

  const rows = responsesQuery.data?.data ?? []
  const total = responsesQuery.data?.total ?? 0
  const detailGroups = detailQuery.data?.data ?? []

  const columns = useMemo<ColumnDef<SurveyResponseSummaryDTO>[]>(
    () => [
      {
        accessorKey: 'userId',
        header: t('survey.columnUserId', { defaultValue: 'User' }),
        cell: ({ row }) => (
          <span className="text-zinc-700">
            {t('survey.respondentLabel', {
              defaultValue: 'Respondent {{index}}',
              index: row.index + 1,
            })}
          </span>
        ),
      },
      {
        accessorKey: 'surveyTitle',
        header: t('survey.columnSurveyTitle', { defaultValue: 'Survey' }),
        cell: ({ row }) => (
          <div className="min-w-[220px]">
            <p className="font-medium text-zinc-800">{row.original.surveyTitle || '-'}</p>
          </div>
        ),
      },
      {
        accessorKey: 'business',
        header: t('survey.columnBusiness'),
        // eslint-disable-next-line ts/prefer-nullish-coalescing -- empty string should fall back to '-'
        cell: ({ row }) => <span className="text-zinc-600">{row.original.business || '-'}</span>,
      },
      {
        accessorKey: 'totalResponses',
        header: t('survey.columnTotalResponses', { defaultValue: 'Responses' }),
        cell: ({ row }) => <Badge variant="secondary">{row.original.totalResponses}</Badge>,
      },
      {
        accessorKey: 'submittedAt',
        header: t('survey.columnSubmitted', { defaultValue: 'Submitted' }),
        cell: ({ row }) => formatDateTime(row.original.submittedAt),
      },
    ],
    [t],
  )

  return (
    <PageContainer>
      <PageHeader
        title={t('survey.responsesTitle', { defaultValue: 'Survey responses' })}
        breadcrumbs={[
          { label: t('sidebar.tenants'), path: scopedAdminPath('/tenant') },
          ...(currentTenantId
            ? [
                {
                  label: tenantLoading ? t('common.loading') : tenantName || '-',
                  path: scopedAdminPath(
                    generatePath('/tenant/:tenantId', { tenantId: currentTenantId }),
                  ),
                },
              ]
            : []),
          { label: t('survey.responsesTitle', { defaultValue: 'Survey responses' }) },
        ]}
      />

      {isSuperAdmin && (
        <DataTableToolbar>
          <DataTableFilterField label={t('sidebar.tenants')}>
            <Select
              value={currentTenantId || ''}
              onValueChange={(value) => {
                setActiveTenantId(value)
                setPage(1)
                setSelectedUserId('')
                void navigate({
                  href: scopedAdminPath(
                    generatePath('/tenant/:tenantId/surveys/responses', { tenantId: value }),
                  ),
                })
              }}
            >
              <SelectTrigger className="w-60">
                <SelectValue placeholder={t('sidebar.tenants')} />
              </SelectTrigger>
              <SelectContent>
                {tenantOptions.map((opt: TenantOptionItem) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </DataTableFilterField>
        </DataTableToolbar>
      )}

      {responsesQuery.isError && (
        <Card className="mt-4 border-red-200 bg-red-50/80">
          <CardContent className="pt-6 text-sm text-red-800">
            {t('survey.responsesLoadError', { defaultValue: 'Could not load responses.' })}
          </CardContent>
        </Card>
      )}

      <div className="mt-6">
        <DataTable
          columns={columns}
          data={rows}
          loading={responsesQuery.isLoading}
          emptyText={t('survey.emptyResponses', { defaultValue: 'No responses yet.' })}
          onRowClick={(row) => setSelectedUserId(row.userId)}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage)
              setPageSize(nextPageSize)
            },
          }}
        />
      </div>

      <Sheet open={!!selectedUserId} onOpenChange={(open) => !open && setSelectedUserId('')}>
        <SheetContent side="right" className="w-full overflow-y-auto p-0 sm:max-w-[760px]">
          <div className="border-b border-zinc-200 px-6 py-5">
            <SheetHeader>
              <SheetTitle>
                {t('survey.responseDetailTitle', { defaultValue: 'Response detail' })}
              </SheetTitle>
            </SheetHeader>
          </div>

          <div className="space-y-5 px-6 py-5">
            {detailQuery.isLoading && (
              <div className="space-y-4">
                <Skeleton className="h-24 w-full rounded-lg" />
                <Skeleton className="h-20 w-full rounded-lg" />
                <Skeleton className="h-20 w-full rounded-lg" />
              </div>
            )}

            {detailQuery.isError && (
              <Card className="border-red-200 bg-red-50/80">
                <CardContent className="pt-6 text-sm text-red-800">
                  {t('survey.responsesLoadError', { defaultValue: 'Could not load responses.' })}
                </CardContent>
              </Card>
            )}

            {!detailQuery.isLoading && !detailQuery.isError && detailGroups.length < 1 && (
              <p className="rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-8 text-center text-sm text-zinc-500">
                {t('survey.emptyResponses', { defaultValue: 'No responses yet.' })}
              </p>
            )}

            {detailGroups.map((group) => (
              <section key={group.survey.versionId} className="space-y-4">
                <div className="rounded-lg border border-zinc-200 bg-white p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h3 className="text-base font-semibold text-zinc-900">
                        {group.survey.title || '-'}
                      </h3>
                      {group.survey.description && (
                        <p className="mt-2 text-sm leading-6 text-zinc-600">
                          {group.survey.description}
                        </p>
                      )}
                    </div>
                    {group.survey.business && (
                      <Badge variant="outline">{group.survey.business}</Badge>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  {group.responses.map((response, index) => (
                    <article
                      key={response.id}
                      className="rounded-lg border border-zinc-200 bg-white p-4"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-xs font-medium uppercase text-zinc-500">
                            {t('survey.questionLabel', {
                              defaultValue: 'Question {{index}}',
                              index: index + 1,
                            })}
                          </p>
                          <p className="mt-1 text-sm font-medium leading-6 text-zinc-900">
                            {/* eslint-disable-next-line ts/prefer-nullish-coalescing -- empty string should fall back to '-' */}
                            {response.questionContent || '-'}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {response.type && <Badge variant="secondary">{response.type}</Badge>}
                          {response.languageCode && (
                            <Badge variant="outline">{response.languageCode}</Badge>
                          )}
                        </div>
                      </div>
                      <div className="mt-4 rounded-md bg-zinc-50 px-3 py-2 text-sm text-zinc-800">
                        {/* eslint-disable-next-line ts/prefer-nullish-coalescing -- whitespace-only/empty answer should fall back to '-' */}
                        {response.answerContent?.trim() || '-'}
                      </div>
                      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-zinc-500">
                        <span>{formatDateTime(response.createdAt)}</span>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </PageContainer>
  )
}
